import { useState } from 'react'
import api from '../../../utils/api'
import RecordCard from './RecordCard'

/**
 * Reusable component for selecting a record by ID or metadata search
 */
function RecordSelector({ label, record, onRecordSelected, onClear, error, setError }) {
  const [idInput, setIdInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState('')

  const fetchRecordById = async () => {
    if (!idInput) return

    try {
      setLoading(true)
      setFetchError('')

      const response = await api.get(`admin/reconciliation/record/${idInput}/`)
      onRecordSelected(response.data.record)
      setIdInput('')
    } catch (err) {
      console.error('Error fetching record:', err)
      setFetchError(
        `Record not found: ${err.response?.status === 404 ? 'Record does not exist' : 'Error fetching record'}`
      )
    } finally {
      setLoading(false)
    }
  }

  const searchRecords = async () => {
    if (!searchQuery) return

    try {
      setLoading(true)

      const response = await api.get('admin/reconciliation/search/', {
        params: { query: searchQuery, pageSize: 20 },
      })

      setSearchResults(response.data.results)
    } catch (err) {
      console.error('Error searching records:', err)
      setError(`Failed to search records`)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectFromResults = (selectedRecord) => {
    onRecordSelected(selectedRecord)
    setSearchResults([])
    setSearchQuery('')
  }

  if (record) {
    return <RecordCard record={record} label={label} onClear={onClear} />
  }

  return (
    <div>
      {/* Search by ID */}
      <div className='mb-4'>
        <label className='mb-1 block'>Search By ID:</label>
        <div className='flex gap-2'>
          <input
            type='text'
            value={idInput}
            onChange={(e) => setIdInput(e.target.value)}
            placeholder='Enter Record ID'
            className='flex-grow border p-2'
          />
          <button
            onClick={fetchRecordById}
            className='bg-blue-500 text-white hover:bg-blue-600 rounded px-4 py-2'
            disabled={!idInput || loading}
          >
            Fetch
          </button>
        </div>
        {(fetchError || error) && (
          <p className='text-red-500 mt-1 text-sm'>{fetchError || error}</p>
        )}
      </div>

      {/* Search by metadata */}
      <div className='mt-4 border-t pt-4'>
        <label className='mb-1 block'>or Search By Metadata:</label>
        <div className='flex gap-2'>
          <input
            type='text'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search by title, artist, etc.'
            className='flex-grow border p-2'
          />
          <button
            onClick={searchRecords}
            className='bg-blue-500 text-white hover:bg-blue-600 rounded px-4 py-2'
            disabled={!searchQuery || loading}
          >
            Search
          </button>
        </div>

        {/* Search results table */}
        {searchResults.length > 0 && (
          <div className='mt-2 overflow-hidden rounded border'>
            <table className='min-w-full'>
              <thead className='bg-gray-100'>
                <tr>
                  <th className='px-2 py-1'>Type</th>
                  <th className='px-2 py-1'>Title</th>
                  <th className='px-2 py-1'>Artists</th>
                  <th className='px-2 py-1'>Service</th>
                  <th className='px-2 py-1'>Cloud ID</th>
                  <th className='px-2 py-1'>Action</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((result) => (
                  <tr
                    key={`${result.id}-${result.instanceId}-${result.service}`}
                    className='hover:bg-gray-50 border-t'
                  >
                    <td className='px-2 py-1 capitalize'>{result.mediaType || 'Unknown'}</td>
                    <td className='px-2 py-1'>{result.title}</td>
                    <td className='px-2 py-1'>{result.artists?.join(', ') || 'Unknown'}</td>
                    <td className='px-2 py-1'>{result.service || 'Unknown'}</td>
                    <td className='px-2 py-1'>{result.cloudId || 'None'}</td>
                    <td className='px-2 py-1'>
                      <button
                        onClick={() => handleSelectFromResults(result)}
                        className='bg-blue-500 text-white hover:bg-blue-600 rounded px-2 py-1 text-xs'
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default RecordSelector
